// Serve the members of one uncompressed ZIP archive as files under a URL prefix.
//
// Celldega fetches its LandscapeFiles by relative path under a base URL. A
// Celldega dataset archive (`*.celldega.zip`) stores those files uncompressed,
// so each one is a byte span of the archive. Requests under the prefix become
// absolute byte ranges (`bytes=a-b`) on the archive's presigned S3 URL, which
// browsers send without a CORS preflight. This is a fetch-level adapter for
// third-party code that calls `fetch` itself (including parquet-wasm), so it
// wraps the platform `fetch` rather than going through `apiClient`.

const EOCD_SIGNATURE = 0x06054b50;
const ZIP64_LOCATOR_SIGNATURE = 0x07064b50;
const ZIP64_EOCD_SIGNATURE = 0x06064b50;
const CENTRAL_SIGNATURE = 0x02014b50;
const LOCAL_SIGNATURE = 0x04034b50;
const EOCD_SIZE = 22;
const ZIP64_LOCATOR_SIZE = 20;
const MAX_COMMENT_SIZE = 0xffff;
const LOCAL_HEADER_SIZE = 30;
const STORED = 0;
const SATURATED_32 = 0xffffffff;
const SATURATED_16 = 0xffff;

// A read that starts within this many bytes of the start of a member also fetches the member's local
// header in the same request, so a dataset of tens of thousands of small files costs one request per file.
const COMBINED_READ_START_LIMIT = 4096;
// A combined read is buffered in memory, so it only covers the first bytes of a member.
const COMBINED_READ_END_LIMIT = 4 * 1024 * 1024;
// The local header's extra field can be longer than the central directory's; a combined read allows for this much.
const LOCAL_EXTRA_SLACK = 64;

const CONTENT_TYPES: Record<string, string> = {
  json: "application/json",
  parquet: "application/vnd.apache.parquet",
  webp: "image/webp",
  png: "image/png",
  dzi: "application/xml",
  csv: "text/csv",
};

export interface ZipArchiveFetchOptions {
  /** Base URL Celldega is given; requests under it are served from the archive. */
  prefix: string;
  /** Returns a URL for the archive; called again when S3 answers 403 (an expired presign). */
  resolveArchiveUrl: () => Promise<string>;
  /** The archive's size in bytes, when known, which saves a request and the need for `Content-Range`. */
  archiveSize?: number;
  fetchImpl: typeof fetch;
}

export type ZipArchiveFetch = (
  input: RequestInfo | URL,
  init?: RequestInit
) => Promise<Response | null>;

interface ZipEntry {
  name: string;
  size: number;
  headerOffset: number;
}

interface ArchiveDirectory {
  entries: Map<string, ZipEntry>;
  /** The bytes `start` to `end` (inclusive) of a member's data. */
  readSpan: (
    entry: ZipEntry,
    start: number,
    end: number,
    signal?: AbortSignal | null
  ) => Promise<ReadableStream<Uint8Array> | Uint8Array<ArrayBuffer>>;
}

type ReadRange = (start: number, end: number, signal?: AbortSignal | null) => Promise<Response>;

/** A fetch-shaped function that answers requests under `prefix` and returns null for any other URL. */
export function createZipArchiveFetch(options: ZipArchiveFetchOptions): ZipArchiveFetch {
  const base = options.prefix.replace(/\/$/, "");
  const basePath = new URL(base).pathname;
  let archiveUrl: Promise<string> | null = null;
  let directory: Promise<ArchiveDirectory> | null = null;

  const currentUrl = () => (archiveUrl ??= options.resolveArchiveUrl());

  const readRange: ReadRange = async (start, end, signal) => {
    const request = async () => {
      const url = currentUrl();
      const response = await options.fetchImpl(await url, {
        headers: { Range: `bytes=${start}-${end}` },
        signal,
      });
      return { url, response };
    };
    const first = await request();
    let response = first.response;
    if (response.status === 403) {
      // Reads that met the same expired URL share one new presign.
      if (archiveUrl === first.url) {
        archiveUrl = null;
      }
      response = (await request()).response;
    }
    if (response.status !== 206) {
      throw new Error(
        `the archive did not answer the byte range ${start}-${end} (HTTP ${response.status})`
      );
    }
    return response;
  };

  const openDirectory = () =>
    (directory ??= loadDirectory(readRange, options.archiveSize).catch((error: unknown) => {
      directory = null;
      throw error;
    }));

  const serve = async (url: string, request: Request | null, init: RequestInit) => {
    const path = decodeURIComponent(new URL(url).pathname.slice(basePath.length + 1));
    const method = (init.method ?? request?.method ?? "GET").toUpperCase();
    const signal = init.signal ?? request?.signal;
    const range = new Headers(init.headers ?? request?.headers).get("Range");

    const { entries, readSpan } = await openDirectory();
    const entry = entries.get(path);
    if (!entry) {
      return new Response(null, { status: 404, statusText: `${path} is not in the archive` });
    }

    const headers = {
      "Accept-Ranges": "bytes",
      "Content-Type": CONTENT_TYPES[path.split(".").pop() ?? ""] ?? "application/octet-stream",
    };
    if (method === "HEAD") {
      return new Response(null, {
        status: 200,
        headers: { ...headers, "Content-Length": String(entry.size) },
      });
    }

    const span = parseRange(range, entry.size);
    if (span === "unsatisfiable") {
      return new Response(null, {
        status: 416,
        headers: { ...headers, "Content-Range": `bytes */${entry.size}` },
      });
    }
    const [start, end] = span ?? [0, entry.size - 1];
    if (end < start) {
      return new Response(new Uint8Array(0), {
        status: 200,
        headers: { ...headers, "Content-Length": "0" },
      });
    }

    const body = await readSpan(entry, start, end, signal);
    return new Response(body, {
      status: span ? 206 : 200,
      headers: {
        ...headers,
        "Content-Length": String(end - start + 1),
        ...(span ? { "Content-Range": `bytes ${start}-${end}/${entry.size}` } : {}),
      },
    });
  };

  return async (input, init = {}) => {
    const request = input instanceof Request ? input : null;
    const url = request ? request.url : String(input);
    if (!url.startsWith(`${base}/`)) {
      return null;
    }
    const response = await serve(url, request, init);
    // A constructed Response has an empty url, and parquet-wasm parses it.
    Object.defineProperty(response, "url", { value: url });
    return response;
  };
}

const registered = new Map<string, ZipArchiveFetch>();
let platformFetch: typeof fetch | null = null;

/**
 * Serve requests under `prefix` from the archive until the returned function is called.
 * One wrapper around the global `fetch` serves every registered archive; the original
 * `fetch` is restored when the last one is unregistered.
 */
export function registerZipArchive(
  options: Omit<ZipArchiveFetchOptions, "fetchImpl"> & { fetchImpl?: typeof fetch }
): () => void {
  if (registered.has(options.prefix)) {
    throw new Error(`${options.prefix} already serves an archive`);
  }
  if (!platformFetch) {
    const original = globalThis.fetch;
    platformFetch = original;
    globalThis.fetch = async (input, init) => {
      for (const zipFetch of registered.values()) {
        const response = await zipFetch(input, init);
        if (response) {
          return response;
        }
      }
      return original(input, init);
    };
  }
  const fetchImpl = options.fetchImpl ?? platformFetch.bind(globalThis);
  registered.set(options.prefix, createZipArchiveFetch({ ...options, fetchImpl }));

  return () => {
    registered.delete(options.prefix);
    if (registered.size === 0 && platformFetch) {
      globalThis.fetch = platformFetch;
      platformFetch = null;
    }
  };
}

/**
 * [start, end] within a member of `size` bytes; null for no Range or a multi-part
 * one (served whole); "unsatisfiable" when it starts past the end.
 */
function parseRange(
  header: string | null,
  size: number
): [number, number] | "unsatisfiable" | null {
  if (!header) {
    return null;
  }
  const match = /^bytes=(\d*)-(\d*)$/.exec(header.trim());
  if (!match || (match[1] === "" && match[2] === "")) {
    return null;
  }
  if (match[1] === "") {
    return [Math.max(0, size - Number(match[2])), size - 1];
  }
  const start = Number(match[1]);
  if (start >= size) {
    return "unsatisfiable";
  }
  const end = match[2] === "" ? size - 1 : Math.min(Number(match[2]), size - 1);
  return start > end ? "unsatisfiable" : [start, end];
}

async function readView(
  readRange: ReadRange,
  start: number,
  end: number,
  signal?: AbortSignal | null
) {
  return new DataView(await (await readRange(start, end, signal)).arrayBuffer());
}

async function archiveSizeFromProbe(readRange: ReadRange) {
  const probe = await readRange(0, 0);
  const total = Number(/\/(\d+)$/.exec(probe.headers.get("Content-Range") ?? "")?.[1]);
  await probe.arrayBuffer();
  if (!Number.isFinite(total)) {
    throw new Error("the archive response has no Content-Range total size");
  }
  return total;
}

async function loadDirectory(readRange: ReadRange, knownSize?: number): Promise<ArchiveDirectory> {
  const total = knownSize ?? (await archiveSizeFromProbe(readRange));
  const tailStart = Math.max(0, total - (EOCD_SIZE + MAX_COMMENT_SIZE + ZIP64_LOCATOR_SIZE));
  const tail = await readView(readRange, tailStart, total - 1);

  let eocd = -1;
  for (let i = tail.byteLength - EOCD_SIZE; i >= 0; i -= 1) {
    if (tail.getUint32(i, true) === EOCD_SIGNATURE) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) {
    throw new Error("not a ZIP archive: no end of central directory record");
  }

  let count = tail.getUint16(eocd + 10, true);
  let directorySize = tail.getUint32(eocd + 12, true);
  let directoryOffset = tail.getUint32(eocd + 16, true);
  if (
    count === SATURATED_16 ||
    directorySize === SATURATED_32 ||
    directoryOffset === SATURATED_32
  ) {
    const locator = eocd - ZIP64_LOCATOR_SIZE;
    if (locator < 0 || tail.getUint32(locator, true) !== ZIP64_LOCATOR_SIGNATURE) {
      throw new Error("ZIP64 archive without a ZIP64 end of central directory locator");
    }
    const recordOffset = Number(tail.getBigUint64(locator + 8, true));
    const record = await readView(readRange, recordOffset, recordOffset + 55);
    if (record.getUint32(0, true) !== ZIP64_EOCD_SIGNATURE) {
      throw new Error("ZIP64 end of central directory record not found");
    }
    count = Number(record.getBigUint64(32, true));
    directorySize = Number(record.getBigUint64(40, true));
    directoryOffset = Number(record.getBigUint64(48, true));
  }

  const directory =
    directoryOffset >= tailStart
      ? new DataView(tail.buffer, tail.byteOffset + directoryOffset - tailStart, directorySize)
      : await readView(readRange, directoryOffset, directoryOffset + directorySize - 1);
  const entries = parseCentralDirectory(directory, count);

  const offsets = new Map<number, Promise<number>>();
  const dataOffset = (entry: ZipEntry, signal?: AbortSignal | null) => {
    const known = offsets.get(entry.headerOffset);
    if (known) {
      return known;
    }
    const pending = readView(
      readRange,
      entry.headerOffset,
      entry.headerOffset + LOCAL_HEADER_SIZE - 1,
      signal
    ).then((local) => {
      if (local.getUint32(0, true) !== LOCAL_SIGNATURE) {
        throw new Error(`no local header for ${entry.name} at ${entry.headerOffset}`);
      }
      return (
        entry.headerOffset +
        LOCAL_HEADER_SIZE +
        local.getUint16(26, true) +
        local.getUint16(28, true)
      );
    });
    pending.catch(() => offsets.delete(entry.headerOffset));
    offsets.set(entry.headerOffset, pending);
    return pending;
  };
  const encoder = new TextEncoder();

  /** One request for the local header and the data; null when the data ran past what was fetched. */
  const readWithHeader = async (
    entry: ZipEntry,
    start: number,
    end: number,
    signal?: AbortSignal | null
  ) => {
    const headerLength = LOCAL_HEADER_SIZE + encoder.encode(entry.name).length;
    const response = await readRange(
      entry.headerOffset,
      entry.headerOffset + headerLength + LOCAL_EXTRA_SLACK + end,
      signal
    );
    const bytes = new Uint8Array(await response.arrayBuffer());
    const local = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (bytes.length < LOCAL_HEADER_SIZE || local.getUint32(0, true) !== LOCAL_SIGNATURE) {
      throw new Error(`no local header for ${entry.name} at ${entry.headerOffset}`);
    }
    const dataStart = LOCAL_HEADER_SIZE + local.getUint16(26, true) + local.getUint16(28, true);
    offsets.set(entry.headerOffset, Promise.resolve(entry.headerOffset + dataStart));
    return dataStart + end < bytes.length
      ? bytes.subarray(dataStart + start, dataStart + end + 1)
      : null;
  };

  const readSpan: ArchiveDirectory["readSpan"] = async (entry, start, end, signal) => {
    if (
      !offsets.has(entry.headerOffset) &&
      start < COMBINED_READ_START_LIMIT &&
      end < COMBINED_READ_END_LIMIT
    ) {
      const combined = await readWithHeader(entry, start, end, signal);
      if (combined) {
        return combined;
      }
    }
    const memberStart = await dataOffset(entry, signal);
    const upstream = await readRange(memberStart + start, memberStart + end, signal);
    if (!upstream.body) {
      throw new Error(`the archive answered ${entry.name} bytes ${start}-${end} with no body`);
    }
    return upstream.body;
  };
  return { entries, readSpan };
}

function parseCentralDirectory(view: DataView, count: number) {
  const decoder = new TextDecoder();
  const entries = new Map<string, ZipEntry>();
  let at = 0;
  for (let i = 0; i < count; i += 1) {
    if (view.getUint32(at, true) !== CENTRAL_SIGNATURE) {
      throw new Error(`central directory entry ${i} is malformed`);
    }
    const method = view.getUint16(at + 10, true);
    let compressedSize = view.getUint32(at + 20, true);
    let size = view.getUint32(at + 24, true);
    const nameLength = view.getUint16(at + 28, true);
    const extraLength = view.getUint16(at + 30, true);
    const commentLength = view.getUint16(at + 32, true);
    let headerOffset = view.getUint32(at + 42, true);
    const name = decoder.decode(new Uint8Array(view.buffer, view.byteOffset + at + 46, nameLength));

    // ZIP64 extended information holds only the saturated fields, in this order.
    let extra = at + 46 + nameLength;
    const extraEnd = extra + extraLength;
    while (extra + 4 <= extraEnd) {
      const id = view.getUint16(extra, true);
      const length = view.getUint16(extra + 2, true);
      if (id === 0x0001) {
        let field = extra + 4;
        const next = () => {
          const value = Number(view.getBigUint64(field, true));
          field += 8;
          return value;
        };
        if (size === SATURATED_32) {
          size = next();
        }
        if (compressedSize === SATURATED_32) {
          compressedSize = next();
        }
        if (headerOffset === SATURATED_32) {
          headerOffset = next();
        }
      }
      extra += 4 + length;
    }

    if (method !== STORED) {
      throw new Error(`${name} is compressed; a Celldega archive stores members uncompressed`);
    }
    if (!name.endsWith("/")) {
      entries.set(name, { name, size, headerOffset });
    }
    at = extraEnd + commentLength;
  }
  return entries;
}
